// File: client/src/hooks/useAttribution.js
// Captures traffic source attribution on page load and persists it
// (first-touch) through the booking flow. Ported from chatwithdrStaging
// with localStorage / sessionStorage keys renamed to fu_* to avoid
// collisions if the same browser visits both sites.

const STORAGE_KEY = 'fu_attribution';
const SESSION_TRACKED_KEY = 'fu_visit_tracked';

const TRACK_API_URL = process.env.NODE_ENV === 'production'
  ? window.location.origin + '/api/track/visit'
  : 'http://localhost:8080/api/track/visit';

const AI_SOURCES = {
  'chatgpt.com': 'ChatGPT',
  'chat.openai.com': 'ChatGPT',
  'perplexity.ai': 'Perplexity',
  'claude.ai': 'Claude',
  'copilot.microsoft.com': 'Microsoft Copilot',
  'gemini.google.com': 'Google Gemini',
  'you.com': 'You.com',
  'phind.com': 'Phind',
  'bard.google.com': 'Google Gemini',
  'poe.com': 'Poe'
};

const SEARCH_ENGINES = {
  'google.com': 'Google',
  'google.co': 'Google',
  'bing.com': 'Bing',
  'yahoo.com': 'Yahoo',
  'duckduckgo.com': 'DuckDuckGo',
  'baidu.com': 'Baidu',
  'yandex.com': 'Yandex',
  'ecosia.org': 'Ecosia'
};

const SOCIAL_PLATFORMS = {
  'facebook.com': 'Facebook',
  'fb.com': 'Facebook',
  'instagram.com': 'Instagram',
  'twitter.com': 'X (Twitter)',
  'x.com': 'X (Twitter)',
  't.co': 'X (Twitter)',
  'linkedin.com': 'LinkedIn',
  'reddit.com': 'Reddit',
  'tiktok.com': 'TikTok',
  'snapchat.com': 'Snapchat',
  'pinterest.com': 'Pinterest',
  'youtube.com': 'YouTube',
  'threads.net': 'Threads',
  'whatsapp.com': 'WhatsApp'
};

const UTM_SOURCE_MAP = {
  google: 'Google Ads',
  facebook: 'Meta (Facebook)',
  fb: 'Meta (Facebook)',
  instagram: 'Meta (Instagram)',
  ig: 'Meta (Instagram)',
  meta: 'Meta',
  snapchat: 'Snapchat Ads',
  snap: 'Snapchat Ads',
  reddit: 'Reddit Ads',
  tiktok: 'TikTok Ads',
  linkedin: 'LinkedIn Ads',
  twitter: 'X (Twitter) Ads',
  x: 'X (Twitter) Ads',
  bing: 'Microsoft Ads',
  pinterest: 'Pinterest Ads',
  youtube: 'YouTube Ads'
};

function getHostname(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
}

function matchDomain(hostname, domainMap) {
  for (const [domain, name] of Object.entries(domainMap)) {
    if (hostname.includes(domain)) return name;
  }
  return null;
}

function classifySource(data) {
  const { utm_source, utm_medium, gclid, gbraid, wbraid, fbclid, sclid, ttclid, referrer } = data;
  const utmSrc = (utm_source || '').toLowerCase();
  const utmMed = (utm_medium || '').toLowerCase();
  const refHost = getHostname(referrer || '');

  if (gclid || gbraid || wbraid) return { type: 'Paid', platform: 'Google Ads' };
  if (fbclid) return { type: 'Paid', platform: 'Meta (Facebook/Instagram)' };
  if (sclid) return { type: 'Paid', platform: 'Snapchat Ads' };
  if (ttclid) return { type: 'Paid', platform: 'TikTok Ads' };

  if (['cpc', 'ppc', 'paid', 'paidsocial', 'paid_social', 'cpm', 'display'].includes(utmMed)) {
    return { type: 'Paid', platform: UTM_SOURCE_MAP[utmSrc] || `${utm_source || 'Unknown'} Ads` };
  }
  if (utmMed === 'social' || utmMed === 'organic_social') {
    const platform =
      SOCIAL_PLATFORMS[refHost] ||
      UTM_SOURCE_MAP[utmSrc]?.replace(' Ads', '') ||
      utm_source ||
      'Social';
    return { type: 'Social', platform };
  }
  if (utmMed === 'email') return { type: 'Email', platform: utm_source || 'Email' };
  if (utmMed === 'referral') return { type: 'Referral', platform: utm_source || refHost || 'Referral' };

  if (utmSrc) {
    if (UTM_SOURCE_MAP[utmSrc]) return { type: 'Paid', platform: UTM_SOURCE_MAP[utmSrc] };
    const ai = matchDomain(utmSrc, AI_SOURCES);
    if (ai) return { type: 'AI Search', platform: ai };
    const se = matchDomain(utmSrc, SEARCH_ENGINES);
    if (se) return { type: 'Organic Search', platform: se };
    const soc = matchDomain(utmSrc, SOCIAL_PLATFORMS);
    if (soc) return { type: 'Social', platform: soc };
    return { type: 'Referral', platform: utm_source };
  }

  if (refHost) {
    const ai = matchDomain(refHost, AI_SOURCES);
    if (ai) return { type: 'AI Search', platform: ai };
    const se = matchDomain(refHost, SEARCH_ENGINES);
    if (se) return { type: 'Organic Search', platform: se };
    const soc = matchDomain(refHost, SOCIAL_PLATFORMS);
    if (soc) return { type: 'Social', platform: soc };
    return { type: 'Referral', platform: refHost };
  }

  return { type: 'Direct', platform: 'Direct' };
}

function collectAttributionData() {
  const params = new URLSearchParams(window.location.search);

  const data = {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    gclid: params.get('gclid') || '',
    gbraid: params.get('gbraid') || '',
    wbraid: params.get('wbraid') || '',
    fbclid: params.get('fbclid') || '',
    sclid: params.get('sclid') || '',
    ttclid: params.get('ttclid') || '',
    referrer: document.referrer || '',
    landing_page: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString()
  };

  const c = classifySource(data);
  data.source_type = c.type;
  data.source_platform = c.platform;
  return data;
}

function trackVisit(data) {
  try {
    if (sessionStorage.getItem(SESSION_TRACKED_KEY)) return;
    sessionStorage.setItem(SESSION_TRACKED_KEY, '1');
    const sessionId = `s_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

    fetch(TRACK_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        source_type: data.source_type,
        source_platform: data.source_platform,
        utm_source: data.utm_source,
        utm_medium: data.utm_medium,
        utm_campaign: data.utm_campaign,
        utm_term: data.utm_term,
        utm_content: data.utm_content,
        gclid: data.gclid,
        fbclid: data.fbclid,
        sclid: data.sclid,
        ttclid: data.ttclid,
        referrer: data.referrer,
        landing_page: data.landing_page,
        source_site: 'femaleuti'
      }),
      keepalive: true
    }).catch(() => {});
  } catch (e) {
    // non-blocking
  }
}

export function captureAttribution() {
  try {
    const data = collectAttributionData();
    trackVisit(data);
    const existing = localStorage.getItem(STORAGE_KEY);
    if (!existing) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch (e) {
    console.log('Attribution capture error (non-blocking):', e);
  }
}

export function getAttribution() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // ignore
  }
  return {
    source_type: 'Direct',
    source_platform: 'Direct',
    landing_page: '',
    referrer: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: ''
  };
}

export function clearAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

export default { captureAttribution, getAttribution, clearAttribution };
